// The Allocated / Admitted / Discharged views — one table, three statuses.
// Allocated is the live census (every patient currently holding a bed);
// Admitted and Discharged are the same rows at their later admission statuses.

import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { viewFinalizedSummary } from "../../utils/viewFinalizedSummary";
import RegisterToolbar from "./RegisterToolbar";
import { fmt } from "./registerUtils";

const STATUS_COLORS = {
  Allocated: "warning",
  Admitted: "info",
  Discharged: "success",
};

const COLUMNS = [
  { header: "Admission ID", get: (r) => r.admissionId ?? "-" },
  { header: "Patient Name", get: (r) => r.patientName },
  { header: "UHID", get: (r) => r.uhid || "-" },
  { header: "Age", get: (r) => (r.age != null ? r.age : "-") },
  { header: "Gender", get: (r) => r.gender || "-" },
  { header: "Ward", get: (r) => r.ward },
  { header: "Department", get: (r) => r.department },
  { header: "Bed Type", get: (r) => r.bedTypeName },
  { header: "Admitting Doctor", get: (r) => r.admittingDoctor },
  { header: "Admitted At", get: (r) => fmt(r.admittedAt) },
  { header: "Status", get: (r) => r.status },
];

export default function PatientListView({ title, rows, loading, onRefresh, showSummary = false }) {
  // Admission whose finalized summary PDF is currently being fetched (null = idle).
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const colCount = showSummary ? 8 : 7;

  // Open the finalized discharge-summary PDF (the "Authenticate & Finalize"
  // output stored in S3), fetched as a short-lived presigned URL. `found:false`
  // means the summary was never finalized for this admission.
  const handleViewSummary = async (admissionId) => {
    setPdfLoadingId(admissionId);
    try {
      await viewFinalizedSummary(admissionId);
    } finally {
      setPdfLoadingId(null);
    }
  };

  return (
    <Box>
      <RegisterToolbar
        title={title}
        columns={COLUMNS}
        rows={rows}
        loading={loading}
        onRefresh={onRefresh}
      />

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Admission</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Ward / Bed</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Admitting Doctor</TableCell>
                <TableCell>Admitted</TableCell>
                <TableCell>Status</TableCell>
                {showSummary && <TableCell align="right">Discharge Summary</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                    {loading ? "Loading…" : "No patients found"}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.admissionId}>
                  <TableCell>{r.admissionId}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 26, height: 26, fontSize: 11 }}>
                        {r.patientName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {r.patientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[r.uhid, r.age != null ? `${r.age}${r.gender}` : null]
                            .filter(Boolean)
                            .join(" · ") || "-"}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.ward}</Typography>
                    {r.bedTypeName !== "—" && (
                      <Typography variant="caption" color="text.secondary">
                        {r.bedTypeName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.department}</TableCell>
                  <TableCell>{r.admittingDoctor}</TableCell>
                  <TableCell>{fmt(r.admittedAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || "default"} />
                  </TableCell>
                  {showSummary && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={pdfLoadingId === r.admissionId}
                        startIcon={
                          pdfLoadingId === r.admissionId ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DescriptionOutlinedIcon />
                          )
                        }
                        onClick={() => handleViewSummary(r.admissionId)}
                        sx={{ textTransform: "none" }}
                      >
                        View Summary
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
